// sign_ph_cancellation (WF-017, CORE-070/071). The nominated PH cancels the permit and
// states whether the work is complete. Work stops at once, before the RS closes out.
import { useId, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Banner, Button, Field, KeyValue, RadioGroup, TextArea, TextInput } from '@/components/ui'
import { CANCELLATION_REASONS, PH_CANCELLATION_DECLARATIONS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { useCommand } from '@/lib/queries'
import {
  buildCancellationArgs,
  cancellationHints,
  cancellationUnanswered,
  EMPTY_CANCELLATION,
  expectedVersionFor,
  personText,
  type CancellationInput,
  permitNeedsIsolation,
} from '../logic'
import { CommandFailure, HintList, NotAvailable, PanelFooter, Question, SigningStatement, TickList, Unanswered, YesNo } from '../parts'

const REASON_OPTIONS = CANCELLATION_REASONS.map((o) => ({ value: o.code, label: o.label }))

export function PhCancellationPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cmd = useCommand('sign_ph_cancellation')
  const conditionId = useId()
  const groupId = useId()
  const [input, setInput] = useState<CancellationInput>(EMPTY_CANCELLATION)

  const revision = bundle.revisions.find((r) => r.id === bundle.permit.current_revision_id) ?? null
  const ph = revision?.parties.find((p) => p.role_code === 'PH')?.person ?? null
  const missing = cancellationUnanswered(input)
  const hints = cancellationHints(input)
  const workText =
    input.work_complete === null ? 'whether the work is complete' : input.work_complete ? 'that the work is complete' : 'that the work is NOT complete'

  const edit = (patch: Partial<CancellationInput>) => {
    // Changed input is a new intent: never resend it under the old request key.
    if (cmd.error) cmd.resetIntent()
    setInput((prev) => ({ ...prev, ...patch }))
  }

  const submit = async () => {
    const args = buildCancellationArgs(bundle.permit.id, input)
    if (!args) return
    try {
      await cmd.run(args, expectedVersionFor('sign_ph_cancellation', bundle))
      onDone()
    } catch {
      /* shown by CommandFailure */
    }
  }

  return (
    <div className="space-y-4">
      <Banner tone="bad" title="Signing cancels this permit - work stops immediately">
        <p>
          No more work may happen under this permit once you sign, even though the Responsible Supervisor still has to inspect the site and
          close out. You can sign even if the authorisation or today&apos;s validation has expired, or a gas test is missing.
          <RuleRefs ruleIds={['CORE-070', 'CORE-071']} />
        </p>
      </Banner>

      <p className="text-sm text-ink-soft">
        Only the nominated Permit Holder can sign{ph ? `: ${personText(ph)}` : ''}. Nobody can sign for the PH.
      </p>

      <NotAvailable action={action} />

      <Question legend="Why are you cancelling?" required>
        <RadioGroup name={`${groupId}-reason`} value={input.reason_code || null} onChange={(v) => edit({ reason_code: v })} options={REASON_OPTIONS} />
      </Question>

      <Question legend="Is the work complete?" required>
        <YesNo
          name={`${groupId}-work-complete`}
          value={input.work_complete}
          onChange={(v) => edit({ work_complete: v })}
          yes={{ label: 'Yes - the work is finished', hint: 'The site is safe and all tools and equipment are removed.' }}
          no={{ label: 'No - the work is not finished', hint: 'A new permit is needed to continue it.' }}
        />
      </Question>

      <Question legend="Are all tools and equipment removed?" required>
        <YesNo
          name={`${groupId}-tools-removed`}
          value={input.tools_equipment_removed}
          onChange={(v) => edit({ tools_equipment_removed: v })}
          yes={{ label: 'Yes - all removed' }}
          no={{ label: 'No - some remain', hint: 'List what remains, and where, in the worksite condition below.' }}
        />
      </Question>

      <HintList hints={hints} />

      {input.work_complete === false ? (
        <Field
          label="Permit on which the incomplete work will continue"
          required
          htmlFor={`${groupId}-continuation`}
          hint='Give the permit number, or write "new permit to be raised". Required for incomplete work (GOV-035).'
        >
          <TextInput
            id={`${groupId}-continuation`}
            value={input.continuation_reference}
            onChange={(e) => edit({ continuation_reference: e.target.value })}
            placeholder="e.g. DEMO-B-2026-00002, or new permit to be raised"
          />
        </Field>
      ) : null}

      <Field label="How are you leaving the worksite?" required htmlFor={conditionId} hint="What a person arriving at the site would find.">
        <TextArea
          id={conditionId}
          value={input.worksite_condition}
          onChange={(e) => edit({ worksite_condition: e.target.value })}
          placeholder="e.g. Area clean, barriers removed, supports SYN-PS-101 to 103 cleaned; no tools left"
        />
      </Field>

      <Question legend="Your declarations" required hint="Tick each one only if it is true.">
        <TickList options={PH_CANCELLATION_DECLARATIONS} value={input.declarations} onChange={(declarations) => edit({ declarations })} />
      </Question>

      <KeyValue
        items={[
          [
            'Isolations',
            permitNeedsIsolation(bundle)
              ? 'This permit needed isolation. Cancelling removes no isolation: it stays in place until the RS records its disposition at closeout.'
              : 'None - this permit needed no isolation',
          ],
        ]}
      />

      <SigningStatement>
        <p>
          You sign as <strong>Permit Holder</strong>: you cancel this permit for the reason you chose, you state {workText}, and
          each declaration you ticked is true.
        </p>
        <p>From this moment no work may continue under this permit.</p>
      </SigningStatement>

      <CommandFailure error={cmd.error} />
      <Unanswered items={missing} />

      <PanelFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => void submit()} loading={cmd.isPending} disabled={missing.length > 0 || !action.enabled}>
          Sign cancellation as Permit Holder
        </Button>
      </PanelFooter>
    </div>
  )
}
